import { useCallback, useMemo } from 'react';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useNavigateToFormSubmission from 'shared/hooks/useNavigateToFormSubmission';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  FormSubmissionFragment,
  useGetFormSubmissionQuery,
  useSubscribeToFormSubmissionSubscription,
} from 'shared/types/graphql';

// We construct a type out of the return type in order to have the types flow from here

interface UseFormSubmissionInput {
  formSubmissionId: string | undefined | null;
  subscribeWhenUnsubmitted?: boolean;
}

type UseFormSubmissionHookReturn = {
  data: FormSubmissionFragment | undefined;
  loading: boolean;
  openFormSubmissionEditor: (clinicPetParent: string) => Promise<void>;
  reload: () => void;
};

const useFormSubmission = ({
  formSubmissionId,
  subscribeWhenUnsubmitted = false,
}: UseFormSubmissionInput): UseFormSubmissionHookReturn => {
  const { currentClinicId } = useClinicUser();

  const { data, loading, called, refetch } = useGetFormSubmissionQuery({
    variables: { formSubmissionId: formSubmissionId || '', clinicId: currentClinicId || '' },
    skip: !formSubmissionId || !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });
  const formSubmission = useMemo(() => data?.findUniqueFormSubmission || undefined, [data]);

  const shouldSubscribe =
    subscribeWhenUnsubmitted && formSubmissionId && called && formSubmission && !formSubmission.submittedAt;
  useSubscribeToFormSubmissionSubscription({
    variables: { id: formSubmissionId || '' },
    skip: !shouldSubscribe,
  });

  const reload = useCallback(() => {
    if (formSubmissionId) {
      refetch();
    }
  }, [formSubmissionId, refetch]);

  const { navigateToFormSubmission } = useNavigateToFormSubmission();

  const openFormSubmissionEditor = useCallback(
    async (clinicPetParentId: string) => {
      if (!formSubmissionId) return;
      navigateToFormSubmission({ clinicPetParentId, formSubmissionId });
    },
    [formSubmissionId, navigateToFormSubmission],
  );

  return {
    data: formSubmission,
    loading,
    openFormSubmissionEditor,
    reload,
  };
};

export default useFormSubmission;
